import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getCenterScopedUrl } from "../../utils/centerScope";

const BASE_URL = "https://mis.empowerplus.in";

const emptyForm = {
    userId: "",
    centerId: "",
    age: "",
    address: "",
    photo: "",
    phoneNo: "",
    gender: "",
    generalInfo: ""
};

export default function UserMappingPage() {
    const token = localStorage.getItem("token");
    const [msg, setMsg] = useState({ type: "", text: "" });
    const [rows, setRows] = useState([]);
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);

    const flash = useCallback((type, text) => {
        setMsg({ type, text });
    }, []);

    const authHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    const jsonHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
    }), [token]);

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : [];
        } catch {
            return text;
        }
    };

    const fetchMappings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/user-mappings"), { headers: authHeaders });
            const data = await safeJson(res);
            setRows(Array.isArray(data) ? data : data?.content || []);
        } catch {
            setRows([]);
            flash("err", "Failed to load user mappings");
        } finally {
            setLoading(false);
        }
    }, [authHeaders, flash]);

    const fetchCenters = useCallback(async () => {
        try {
            const res = await fetch(`${BASE_URL}/api/centers`, { headers: authHeaders });
            const data = await safeJson(res);
            setCenters(Array.isArray(data) ? data : data?.content || []);
        } catch {
            setCenters([]);
        }
    }, [authHeaders]);

    useEffect(() => {
        fetchMappings();
        fetchCenters();
    }, [fetchCenters, fetchMappings]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) =>
            String(row.id || "").toLowerCase().includes(q) ||
            String(row.userId || "").toLowerCase().includes(q) ||
            String(row.fullName || "").toLowerCase().includes(q) ||
            String(row.email || "").toLowerCase().includes(q) ||
            String(row.role || "").toLowerCase().includes(q) ||
            String(row.centerId || "").toLowerCase().includes(q) ||
            String(row.phoneNo || "").toLowerCase().includes(q)
        );
    }, [rows, search]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id);
        setForm({
            userId: row.userId || "",
            centerId: row.centerId || "",
            age: row.age ?? "",
            address: row.address || "",
            photo: row.photo || "",
            phoneNo: row.phoneNo || "",
            gender: row.gender || "",
            generalInfo: row.generalInfo || ""
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setEditingId(null);
        setForm(emptyForm);
    };

    const saveMapping = async () => {
        if (!form.userId || !form.centerId) {
            flash("err", "User ID and Center ID are required");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                userId: Number(form.userId),
                centerId: form.centerId,
                age: form.age === "" ? null : Number(form.age),
                address: form.address || null,
                photo: form.photo || null,
                phoneNo: form.phoneNo || null,
                gender: form.gender || null,
                generalInfo: form.generalInfo || null
            };

            const res = await fetch(`${BASE_URL}/api/user-mappings`, {
                method: "POST",
                headers: jsonHeaders,
                body: JSON.stringify(payload)
            });

            const data = await safeJson(res);
            if (!res.ok) {
                throw new Error(data?.message || data || "Failed to save mapping");
            }

            flash("ok", editingId ? "User mapping updated" : "User mapping created");
            closeModal();
            fetchMappings();
        } catch (error) {
            flash("err", error.message || "Failed to save mapping");
        } finally {
            setSaving(false);
        }
    };

    const deleteMapping = async (id) => {
        if (!window.confirm("Delete this user mapping?")) return;
        try {
            const res = await fetch(`${BASE_URL}/api/user-mappings/${id}`, {
                method: "DELETE",
                headers: authHeaders
            });

            if (!res.ok) {
                const data = await safeJson(res);
                throw new Error(data?.message || data || "Delete failed");
            }

            flash("ok", "User mapping deleted");
            fetchMappings();
        } catch (error) {
            flash("err", error.message || "Delete failed");
        }
    };

    return (
        <div className="cmp-page">
            <div className="cmp-header">
                <div>
                    <h1>User Mapping</h1>
                    <p>Map users to centers. Full name, email, and role come automatically from the user record.</p>
                </div>
                <div className="cmp-header-actions">
                    <button className="btn" onClick={fetchMappings} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
                    <button className="btn btn-primary" onClick={openCreate}>Add Mapping</button>
                </div>
            </div>

            {msg.text ? (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            ) : null}

            <div className="card">
                <div className="card-top">
                    <div className="card-title">User Mapping Table</div>
                    <input
                        className="search"
                        placeholder="Search by user, name, email, role, center..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>User ID</th>
                                <th>Full Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Center ID</th>
                                <th>Phone</th>
                                <th>Gender</th>
                                <th>Age</th>
                                <th>Address</th>
                                <th>General Info</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td>{row.userId}</td>
                                    <td>{row.fullName || "-"}</td>
                                    <td>{row.email || "-"}</td>
                                    <td>{row.role || "-"}</td>
                                    <td>{row.centerId || "-"}</td>
                                    <td>{row.phoneNo || "-"}</td>
                                    <td>{row.gender || "-"}</td>
                                    <td>{row.age ?? "-"}</td>
                                    <td>{row.address || "-"}</td>
                                    <td>{row.generalInfo || "-"}</td>
                                    <td className="actions">
                                        <button className="btn btn-sm" onClick={() => openEdit(row)}>Edit</button>
                                        <button className="btn btn-sm btn-danger" onClick={() => deleteMapping(row.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length && (
                                <tr>
                                    <td colSpan="12" className="muted">{loading ? "Loading..." : "No user mappings found"}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && createPortal(
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-title">{editingId ? "Edit User Mapping" : "Add User Mapping"}</div>

                        <div className="form-grid">
                            <div className="field">
                                <label>User ID *</label>
                                <input type="number" value={form.userId} onChange={(e) => setForm((prev) => ({ ...prev, userId: e.target.value }))} />
                            </div>

                            <div className="field">
                                <label>Center ID *</label>
                                {centers.length ? (
                                    <select value={form.centerId} onChange={(e) => setForm((prev) => ({ ...prev, centerId: e.target.value }))}>
                                        <option value="">Select Center</option>
                                        {centers.map((center) => (
                                            <option key={center.centerId} value={center.centerId}>
                                                {center.centerId} - {center.trainingCentreName || center.centerName || "Center"}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input value={form.centerId} onChange={(e) => setForm((prev) => ({ ...prev, centerId: e.target.value }))} />
                                )}
                            </div>

                            <div className="field">
                                <label>Age</label>
                                <input type="number" value={form.age} onChange={(e) => setForm((prev) => ({ ...prev, age: e.target.value }))} />
                            </div>

                            <div className="field">
                                <label>Phone No</label>
                                <input value={form.phoneNo} onChange={(e) => setForm((prev) => ({ ...prev, phoneNo: e.target.value }))} />
                            </div>

                            <div className="field">
                                <label>Gender</label>
                                <select value={form.gender} onChange={(e) => setForm((prev) => ({ ...prev, gender: e.target.value }))}>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div className="field">
                                <label>Photo URL</label>
                                <input value={form.photo} onChange={(e) => setForm((prev) => ({ ...prev, photo: e.target.value }))} />
                            </div>
                        </div>

                        <div className="field">
                            <label>Address</label>
                            <textarea rows="3" value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} />
                        </div>

                        <div className="field">
                            <label>General Info</label>
                            <textarea rows="3" value={form.generalInfo} onChange={(e) => setForm((prev) => ({ ...prev, generalInfo: e.target.value }))} />
                        </div>

                        <div className="modal-actions">
                            <button className="btn" onClick={closeModal}>Cancel</button>
                            <button className="btn btn-primary" onClick={saveMapping} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <style>{`
                .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter, system-ui, Arial; color:#111827; }
                .cmp-header { display:flex; justify-content:space-between; align-items:flex-end; gap:14px; margin-bottom:12px; }
                .cmp-header h1 { margin:0; font-size:26px; font-weight:900; }
                .cmp-header p { margin:6px 0 0; color:#6b7280; font-size:13px; }
                .cmp-header-actions { display:flex; gap:10px; align-items:center; }
                .flash { margin:12px 0; padding:10px 12px; border-radius:12px; font-weight:800; font-size:13px; }
                .flash-ok { background:#ecfdf5; border:1px solid #a7f3d0; color:#065f46; }
                .flash-err { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; }
                .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; box-shadow:0 1px 2px rgba(16, 24, 40, 0.04); }
                .card-top { display:flex; justify-content:space-between; gap:12px; align-items:center; margin-bottom:10px; }
                .card-title { font-size:15px; font-weight:900; }
                .btn { border:1px solid #e5e7eb; background:#fff; padding:10px 14px; border-radius:12px; font-weight:900; cursor:pointer; }
                .btn-primary { border-color:#2563eb; background:#2563eb; color:#fff; }
                .btn-sm { padding:7px 10px; border-radius:10px; font-size:12px; }
                .btn-danger { border-color:#fca5a5; color:#991b1b; }
                .search { width:340px; max-width:50vw; border:1px solid #e5e7eb; border-radius:12px; padding:10px 12px; outline:none; font-size:14px; }
                .table-wrap { overflow-x:auto; }
                table { width:100%; border-collapse:collapse; }
                th { text-align:left; font-size:12px; color:#6b7280; font-weight:900; padding:10px; border-bottom:1px solid #e5e7eb; background:#fcfcff; white-space:nowrap; }
                td { padding:10px; border-bottom:1px solid #f1f5f9; font-size:13px; white-space:nowrap; }
                td.actions { display:flex; gap:8px; align-items:center; }
                .muted { color:#6b7280; font-weight:800; text-align:center; }
                .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:2147483647; }
                .ep-modal { background:#fff; width:min(760px, 96vw); max-height:90vh; overflow:auto; border-radius:16px; padding:18px; box-shadow:0 30px 80px rgba(0, 0, 0, 0.35); }
                .modal-title { font-size:20px; font-weight:900; margin-bottom:14px; }
                .form-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:14px; }
                .field { display:flex; flex-direction:column; gap:6px; margin-bottom:12px; }
                .field label { font-size:12px; font-weight:800; color:#6b7280; }
                .field input, .field select, .field textarea { padding:10px 12px; border-radius:10px; border:1px solid #e5e7eb; font-size:14px; background:#fff; }
                .modal-actions { margin-top:14px; display:flex; justify-content:flex-end; gap:10px; }
                @media (max-width: 768px) {
                    .cmp-header, .card-top { flex-direction:column; align-items:stretch; }
                    .cmp-header-actions { justify-content:flex-start; }
                    .search { width:100%; max-width:none; }
                    .form-grid { grid-template-columns:1fr; }
                }
            `}</style>
        </div>
    );
}
